import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useHouseStore } from '../store/houseStore';
import { useImage } from '../hooks/useImage';
import { fetchHouseDetails } from '../api/houseApi';
import { Save, Trash2, ArrowLeft, Home, Frown, Meh, Smile, RefreshCw, X, Check, MapPin, DollarSign, PenTool } from 'lucide-react';

const EditHouse: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getHouse, updateHouse, removeHouse } = useHouseStore();
  const [formData, setFormData] = useState<any>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [refreshData, setRefreshData] = useState<any>(null);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('info');
  
  // Use hook to get image URL (blob or remote)
  const displayImageUrl = useImage(formData?.imageId, formData?.imageUrl);

  useEffect(() => {
    if (id) {
      const house = getHouse(id);
      if (house) {
        setFormData({ ...house });
      } else {
        navigate('/');
      }
    }
  }, [id, getHouse, navigate]);

  if (!formData) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleFeelChange = (value: number) => {
    setFormData((prev: any) => ({ ...prev, feel: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (id && formData) {
      updateHouse(id, formData);
      navigate('/');
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      if (id) {
        removeHouse(id);
        navigate('/');
      }
    }
  };

  const handleRefresh = async () => {
    if (!formData.url) return;
    setRefreshing(true);
    try {
      const newData = await fetchHouseDetails(formData.url);
      setRefreshData(newData);
      
      // Pre-select fields that have changed
      const protectedFields = ['id', 'url', 'sourceUrl', 'imageId', 'notes', 'myPricePrediction', 'feel', 'status'];
      
      const changedFields = Object.keys(newData).filter(key => {
        if (protectedFields.includes(key)) return false;
        
        const newVal = (newData as any)[key];
        const oldVal = formData[key];
        // Simple comparison, ignore if both are falsy
        if (!newVal && !oldVal) return false;
        return newVal !== oldVal;
      });
      
      setSelectedFields(changedFields);
      setShowRefreshModal(true);
    } catch (error) {
      console.error('Failed to refresh data', error);
      alert('Failed to fetch latest data from source.');
    } finally {
      setRefreshing(false);
    }
  };

  const applyRefresh = async () => {
    if (!refreshData || !id) return;
    
    const updates: any = {};
    selectedFields.forEach(field => {
      updates[field] = refreshData[field];
    });
    
    // Update local state
    setFormData((prev: any) => ({ ...prev, ...updates }));

    // Update database immediately
    try {
      await updateHouse(id, updates);
    } catch (error) {
      console.error('Failed to save refreshed data', error);
      alert('Failed to save updates to database. Please try saving manually.');
    }
    
    setShowRefreshModal(false);
    setRefreshData(null);
  };

  const toggleFieldSelection = (field: string) => {
    setSelectedFields(prev => 
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const formatCurrency = (val?: number) => {
    if (!val) return 'N/A';
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', maximumFractionDigits: 0 }).format(val);
  };

  const feelOptions = [
    { value: 1, label: 'Very Bad', emoji: '😫', color: 'bg-red-100 text-red-600 border-red-200' },
    { value: 2, label: 'Bad', emoji: '🙁', color: 'bg-orange-100 text-orange-600 border-orange-200' },
    { value: 3, label: 'Neutral', emoji: '😐', color: 'bg-yellow-100 text-yellow-600 border-yellow-200' },
    { value: 4, label: 'Good', emoji: '🙂', color: 'bg-lime-100 text-lime-600 border-lime-200' },
    { value: 5, label: 'Excellent', emoji: '🤩', color: 'bg-green-100 text-green-600 border-green-200' },
  ];

  const tabs = [
    { id: 'info', label: 'Property Info', icon: Home },
    { id: 'location', label: 'Location', icon: MapPin },
    { id: 'valuation', label: 'Valuation', icon: DollarSign },
    { id: 'notes', label: 'My Notes', icon: PenTool },
  ];

  return (
    <div className="max-w-4xl mx-auto pb-12">
      {/* Snazzy Banner Image */}
      <div className="relative h-80 w-full rounded-2xl overflow-hidden shadow-2xl mb-8 group">
        {displayImageUrl ? (
          <img 
            src={displayImageUrl} 
            alt={formData.address} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center">
            <Home className="w-24 h-24 text-white opacity-20" />
          </div>
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

        {/* Top Controls */}
        <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-start z-10">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center px-4 py-2 bg-black/40 hover:bg-black/60 backdrop-blur-md rounded-full text-white transition-all border border-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </button>
          
          <div className="flex space-x-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`flex items-center px-4 py-2 bg-indigo-600/80 hover:bg-indigo-700 backdrop-blur-md rounded-full text-white transition-all shadow-lg ${refreshing ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> 
              {refreshing ? 'Fetching...' : 'Refresh Data'}
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center px-4 py-2 bg-red-500/80 hover:bg-red-600 backdrop-blur-md rounded-full text-white transition-all shadow-lg"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </button>
          </div>
        </div>

        {/* Bottom Info */}
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white z-10">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 shadow-sm tracking-tight">{formData.address}</h1>
          <div className="flex flex-wrap items-center gap-3 text-white/90">
             <span className="bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-sm font-medium border border-white/20">
               Est. {formatCurrency(formData.estimate)}
             </span>
             {formData.myPricePrediction > 0 && (
               <span className="bg-green-500/90 backdrop-blur-md px-4 py-1.5 rounded-full text-sm font-medium border border-green-400/30 shadow-lg">
                 My Prediction: {formatCurrency(formData.myPricePrediction)}
               </span>
             )}
          </div>
        </div>
      </div>

      <div className="bg-white shadow-xl sm:rounded-xl overflow-hidden border border-gray-100">

        
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {/* Tabs */}
          <div className="px-6 pt-6 pb-0 border-b border-gray-200 flex space-x-6 overflow-x-auto no-scrollbar">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center pb-3 border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`w-4 h-4 mr-2 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <span className="font-medium text-sm">{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="p-8 min-h-[400px]">
            {/* Basic Info Section */}
            {activeTab === 'info' && (
              <div className="animate-fadeIn">

                <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-6">
                  <div className="sm:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                    <input
                      type="number"
                      name="bedrooms"
                      value={formData.bedrooms}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                    <input
                      type="number"
                      name="bathrooms"
                      value={formData.bathrooms}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parking</label>
                    <input
                      type="number"
                      name="parking"
                      value={formData.parking}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Floor Area (m²)</label>
                    <input
                      type="number"
                      name="propertySize"
                      value={formData.propertySize}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Land Area (m²)</label>
                    <input
                      type="number"
                      name="landSize"
                      value={formData.landSize}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Decade Built</label>
                    <input
                      type="text"
                      name="decadeBuilt"
                      value={formData.decadeBuilt || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Construction</label>
                    <input
                      type="text"
                      name="buildingConstruction"
                      value={formData.buildingConstruction || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ownership Type</label>
                    <input
                      type="text"
                      name="ownershipType"
                      value={formData.ownershipType || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Legal Description</label>
                    <input
                      type="text"
                      name="legalDescription"
                      value={formData.legalDescription || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                  
                  <div className="sm:col-span-3">
                    <div className="flex items-center h-full pt-6">
                      <input
                        id="hasDeck"
                        name="hasDeck"
                        type="checkbox"
                        checked={formData.hasDeck || false}
                        onChange={handleCheckboxChange}
                        className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      />
                      <label htmlFor="hasDeck" className="ml-2 block text-sm text-gray-900">
                        Has Deck
                      </label>
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <div className="flex items-center h-full pt-6">
                      <input
                        id="hasLaundry"
                        name="hasLaundry"
                        type="checkbox"
                        checked={formData.hasLaundry || false}
                        onChange={handleCheckboxChange}
                        className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      />
                      <label htmlFor="hasLaundry" className="ml-2 block text-sm text-gray-900">
                        Has Laundry/Workshop
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Location Section */}
            {activeTab === 'location' && (
              <div className="animate-fadeIn">

                <div className="rounded-lg overflow-hidden shadow-sm border border-gray-200 h-64 bg-gray-100 mb-4">
                   <iframe
                     width="100%"
                     height="100%"
                     frameBorder="0"
                     scrolling="no"
                     marginHeight={0}
                     marginWidth={0}
                     src={`https://maps.google.com/maps?q=${encodeURIComponent(formData.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                     title="Property Location"
                   ></iframe>
                </div>
                <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-6">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label>
                    <input
                      type="text"
                      name="suburb"
                      value={formData.suburb || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">View Type</label>
                    <input
                      type="text"
                      name="viewType"
                      value={formData.viewType || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Valuation & Price Section */}
            {activeTab === 'valuation' && (
              <div className="animate-fadeIn">

                <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-6">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimate</label>
                    <input
                      type="number"
                      name="estimate"
                      value={formData.estimate}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Range Low</label>
                    <input
                      type="number"
                      name="priceRangeLow"
                      value={formData.priceRangeLow}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Range High</label>
                    <input
                      type="number"
                      name="priceRangeHigh"
                      value={formData.priceRangeHigh}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Capital Value</label>
                    <input
                      type="number"
                      name="capitalValue"
                      value={formData.capitalValue || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Improvement Value</label>
                    <input
                      type="number"
                      name="improvementValue"
                      value={formData.improvementValue || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Land Value</label>
                    <input
                      type="number"
                      name="landValue"
                      value={formData.landValue || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rental Appraisal Low</label>
                    <input
                      type="number"
                      name="rentalAppraisalLow"
                      value={formData.rentalAppraisalLow || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rental Appraisal High</label>
                    <input
                      type="number"
                      name="rentalAppraisalHigh"
                      value={formData.rentalAppraisalHigh || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rental Yield</label>
                    <input
                      type="text"
                      name="rentalYield"
                      value={formData.rentalYield || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* My Personal Notes Section */}
            {activeTab === 'notes' && (
              <div className="animate-fadeIn">

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      value={formData.status || 'active'}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                    >
                      <option value="active">Active</option>
                      <option value="watching">Watching Only</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">General Feel</label>
                    <div className="flex items-center justify-between max-w-md">
                      {feelOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleFeelChange(option.value)}
                          className={`flex flex-col items-center p-3 rounded-xl transition-all duration-200 ${
                            formData.feel === option.value 
                              ? `${option.color} ring-2 ring-offset-2 ring-indigo-500 scale-110 shadow-md` 
                              : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:scale-105'
                          }`}
                        >
                          <span className="text-3xl mb-1 filter drop-shadow-sm">{option.emoji}</span>
                          <span className="text-xs font-medium">{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">My Price Prediction</label>
                    <div className="relative rounded-md shadow-sm max-w-xs">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        name="myPricePrediction"
                        value={formData.myPricePrediction || ''}
                        onChange={handleChange}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 sm:text-sm border-gray-300 rounded-md border p-2.5"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      name="notes"
                      rows={4}
                      value={formData.notes || ''}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2.5"
                      placeholder="Add your detailed notes here..."
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-6 px-8 pb-8 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="bg-white py-2.5 px-5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex justify-center py-2.5 px-5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Refresh Data Modal */}
      {showRefreshModal && refreshData && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowRefreshModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                    <RefreshCw className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Update Property Data
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Select the fields you want to update with the latest data from the source.
                      </p>
                      
                      <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                        {Object.keys(refreshData).map((key) => {
                          const newVal = refreshData[key];
                          const oldVal = formData[key];
                          if (key === 'id' || key === 'url' || key === 'sourceUrl' || key === 'imageId' || key === 'notes' || key === 'myPricePrediction' || key === 'feel' || key === 'status') return null;
                          
                          // Skip if values are effectively the same
                          if (newVal == oldVal) return null;
                          if (!newVal && !oldVal) return null;

                          return (
                            <div key={key} className="flex items-start p-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer" onClick={() => toggleFieldSelection(key)}>
                              <div className="flex items-center h-5">
                                <input
                                  id={`field-${key}`}
                                  name={`field-${key}`}
                                  type="checkbox"
                                  checked={selectedFields.includes(key)}
                                  onChange={() => toggleFieldSelection(key)}
                                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                              </div>
                              <div className="ml-3 text-sm w-full">
                                <label htmlFor={`field-${key}`} className="font-medium text-gray-700 capitalize cursor-pointer">
                                  {key.replace(/([A-Z])/g, ' $1').trim()}
                                </label>
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                  <div className="text-red-500 line-through text-xs truncate" title={String(oldVal)}>
                                    {String(oldVal || 'Empty')}
                                  </div>
                                  <div className="text-green-600 font-medium text-xs truncate" title={String(newVal)}>
                                    {String(newVal || 'Empty')}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {Object.keys(refreshData).every(key => {
                           const newVal = refreshData[key];
                           const oldVal = formData[key];
                           if (key === 'id' || key === 'url' || key === 'sourceUrl' || key === 'imageId' || key === 'notes' || key === 'myPricePrediction' || key === 'feel' || key === 'status') return true;
                           return newVal == oldVal || (!newVal && !oldVal);
                        }) && (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            No changes detected.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={applyRefresh}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Update Selected
                </button>
                <button
                  type="button"
                  onClick={() => setShowRefreshModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditHouse;
